import { useRouter } from 'next/router';
import { Text, Flex, FlexItem, Button, Icon, useToast } from '@pairy/pds-react';
import React, { useState } from 'react';
import { useLocalStorageValue } from '@pairy/react-hooks';
import { useAtomValue } from 'jotai';

import BoxAccordion from '@/components/User/_components/ui/BoxAccordion';
import useClientTrackList, {
  TrackingLinkClient,
  isLockedItem,
} from '@/hooks/queries/client/useClientTrackList';
import LinkCopyButton from '@/components/User/_components/common/LinkCopyButton';
import { SortType } from '@/hooks/queries/client/useClientList';
import { localStorageKey } from '@/constants/localStorageKey';
import { pulse } from '@/styles/pulseAnimationCss';
import { userInfoAtom } from '@/state/user/atom';
import ProPlanUpgradeModal from '@/components/User/_components/common/ProPlanUpgradeModal';

import * as Styled from './index.styles';
import EditClientsModal from './EditClientsModal';

const OneToOne = () => {
  const router = useRouter();
  const fileId = Number(router.query.fileId);
  const userInfo = useAtomValue(userInfoAtom);
  const toast = useToast();

  const sortType = useLocalStorageValue<SortType>(
    localStorageKey.fileOrLinkDropdown,
  );

  const { data: clientTrackListData } = useClientTrackList(
    {
      type: 'file',
      id: fileId,
      sort: sortType ?? 'most',
    },
    {
      enabled: Boolean(fileId) && sortType != null,
      select: (data) => data.data.data.clients,
      keepPreviousData: true,
    },
  );

  const notSentClients = React.useMemo(
    () =>
      clientTrackListData?.filter(
        (client): client is TrackingLinkClient =>
          !isLockedItem(client) && !client.isShared,
      ),
    [clientTrackListData],
  );

  const [isOpen, setIsOpen] = React.useState(false);
  const [isProUpgradeModalOpen, setIsProUpgradeModalOpen] = useState(false);

  const handleAddNewClient = () => {
    if (userInfo?.subscriptionPlan.name == 'Free' && userInfo.fileCount >= 4) {
      setIsProUpgradeModalOpen(true);
      toast('Free Plan에서는 파일 3개까지 이용 가능합니다.');
      return;
    } else if (
      userInfo?.subscriptionPlan.name === 'Basic' &&
      userInfo.fileCount >= 11
    ) {
      setIsProUpgradeModalOpen(true);

      toast('Basic Plan에서는 파일 10개까지 이용 가능합니다.');
      return;
    }
    setIsOpen(true);
  };

  return (
    <>
      <Button
        variant="black_fill"
        textVariant="lg-medium"
        radius={9999}
        paddingY={16}
        leftIcon={<Icon icon="ph:plus" size={19} />}
        onClick={handleAddNewClient}
        css={clientTrackListData?.length ? undefined : pulse}
      >
        고객에게 보낼 문서 공유 링크 생성하기
      </Button>
      <BoxAccordion
        title={
          <>
            <Text variant="lg-semibold">아직 링크를 보내지 않은 고객</Text>
            <Text variant="md-medium" color="zinc_400" marginLeft={8}>
              {notSentClients?.length}곳
            </Text>
          </>
        }
      >
        {notSentClients &&
          notSentClients.length > 0 &&
          notSentClients.map(({ id, name, hashId }, index) => (
            <Styled.Box
              key={id}
              className={index + 1 === notSentClients.length ? 'last' : ''}
            >
              <Flex justifyContent="space-between" alignItems="center">
                <FlexItem flexGrow={1}>
                  <Text variant="md-bold">{name}</Text>
                </FlexItem>
                <FlexItem>
                  <LinkCopyButton
                    hashId={hashId}
                    clientId={id}
                    id={fileId}
                    type="file"
                  />
                </FlexItem>
              </Flex>
            </Styled.Box>
          ))}
      </BoxAccordion>
      {isOpen && (
        <EditClientsModal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          onLimitExceeded={() => setIsProUpgradeModalOpen(true)}
        />
      )}
      {isProUpgradeModalOpen && (
        <ProPlanUpgradeModal
          onOpenChange={(open: boolean) => {
            setIsProUpgradeModalOpen(open);
          }}
        />
      )}
    </>
  );
};

export default OneToOne;
